import { DateTime, Duration } from 'luxon';

/**
Calendar year in the viewer's default zone (Copyright).
*/
export const getCurrentYear = (): number => DateTime.now().year;

/**
Format an ISO instant in the viewer's zone; invalid input has no display.
*/
export const formatISODate = (iso: string, format: string): string | undefined => {
  const date = DateTime.fromISO(iso);
  return date.isValid ? date.toFormat(format) : undefined;
};

/**
Local calendar date for file metadata, Russian unless explicitly overridden.
*/
export const formatISOToLocaleString = (iso: string, locale: string = 'ru'): string => {
  const date = DateTime.fromISO(iso);
  return date.isValid ? date.setLocale(locale).toLocaleString() : '';
};

/**
Today's local calendar date for files without a creation timestamp.
*/
export const getNowLocaleString = (locale: string = 'ru'): string =>
  DateTime.now().setLocale(locale).toLocaleString();

/**
Fallback countdown deadline as ISO, retaining the viewer's offset.
*/
export const nowPlusMinutes = (minutes: number): string =>
  DateTime.now().plus({ minutes }).toISO() ?? '';

/**
Elapsed seconds (now - ISO); fractional and negative values are preserved.
*/
export const secondsSince = (iso: string): number =>
  DateTime.now().diff(DateTime.fromISO(iso), 'seconds').seconds;

/**
Remaining seconds (ISO - now); negative means overtime.
*/
export const secondsUntil = (iso: string): number =>
  DateTime.fromISO(iso).diff(DateTime.now()).as('seconds');

/**
Elapsed seconds (end - start), comparing instants across ISO offsets.
*/
export const diffInSeconds = (startISO: string, endISO: string): number =>
  DateTime.fromISO(endISO).diff(DateTime.fromISO(startISO), 'seconds').seconds;

/**
Signed remainder; the countdown consumer owns rounding and expired display.
*/
export const diffFromNowInHoursMinutes = (
  targetISO: string,
): { hours: number; minutes: number } => {
  const difference = DateTime.fromISO(targetISO).diff(DateTime.now(), ['hours', 'minutes']);
  return { hours: difference.hours, minutes: difference.minutes };
};

/**
Estimated processing time: omit zero, round partial minutes upwards.
*/
export const formatEstimatedDuration = (totalSeconds: number): string | undefined => {
  if (totalSeconds === 0) return;
  const duration = Duration.fromObject({ seconds: totalSeconds });
  if (duration.as('minutes') >= 1) {
    return `~${Math.ceil(duration.as('minutes'))} мин`;
  }
  return `~${totalSeconds} сек`;
};

/**
Russian plural forms for the non-negative integer counts supplied by callers.
*/
export const plural = (number: number, titles: string[]): string => {
  const cases = [2, 0, 1, 1, 1, 2];
  return titles[
    number % 100 > 4 && number % 100 < 20 ? 2 : cases[Math.min(number % 10, 5)]
  ];
};

/**
 * Meeting time in its entity's IANA zone, independent of the viewer's zone.
 * Without a valid later end, show only the start. Without a valid start/zone,
 * return undefined so the caller chooses its empty state.
 */
export const formatTimeRangeInZone = (
  startAt: string | null,
  endAt: string | null,
  zone: string,
): string | undefined => {
  if (!startAt) return undefined;
  const start = DateTime.fromISO(startAt, { zone });
  if (!start.isValid) return undefined;
  const end = endAt ? DateTime.fromISO(endAt, { zone }) : undefined;
  // A valid start has an offset label; a fallback would create a dead branch (AF-345).
  const zoneLabel = start.offsetNameShort;
  const range = end?.isValid && end > start
    ? `${start.toFormat('HH:mm')} – ${end.toFormat('HH:mm')}`
    : start.toFormat('HH:mm');
  return `${range} ${zoneLabel}`;
};
